import { useEffect, useState } from 'react';

import VectorIcon from 'assets/VectorIcon.svg';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import { Select } from 'common-ui/select';
import Switch from 'common-ui/switch';
import * as Yup from 'yup';

import { ErrorText } from '../OfferModal/OfferModal.styles';
import { FormContainer, SwitchWrapper } from './NotificationModal.styles';

const NotificationModal = ({
  mode = 'create',
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  initialData,
}) => {
  const isEditMode = mode === 'edit';

  const [formData, setFormData] = useState({
    name: '',
    offsetValue: '',
    offsetType: null,
    enabled: true,
  });

  const [errors, setErrors] = useState({
    name: '',
    offsetValue: '',
    offsetType: '',
  });

  const offsetTypeOptions = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
  ];

  useEffect(() => {
    if (isOpen && isEditMode && initialData) {
      setFormData({
        name: initialData.name || '',
        offsetValue: initialData.offsetValue || '',
        offsetType: initialData.offsetType
          ? offsetTypeOptions.find((opt) => opt.value === initialData.offsetType)
          : null,
        enabled: initialData.enabled ?? true,
      });
    } else if (isOpen && !isEditMode) {
      setFormData({ name: '', offsetValue: '', offsetType: null, enabled: true });
      setErrors({ name: '', offsetValue: '', offsetType: '' });
    }
  }, [isOpen, isEditMode, initialData]);

  const handleClose = () => {
    setFormData({ name: '', offsetValue: '', offsetType: null, enabled: true });
    setErrors({ name: '', offsetValue: '', offsetType: '' });
    onClose();
  };

  const handleSubmit = async () => {
    const schema = Yup.object({
      name: Yup.string().trim().required('Name is required').max(50),

      offsetValue: Yup.number()
        .typeError('Must be a number')
        .required('Offset value is required')
        .min(1, 'Must be between 1 and 10000')
        .max(10000, 'Must be between 1 and 10000'),

      offsetType: Yup.object().nullable().required('Offset type is required'),
    });

    try {
      await schema.validate(formData, { abortEarly: false });

      const dataToSend = {
        name: formData.name.trim(),
        offsetValue: Number(formData.offsetValue),
        offsetType: formData.offsetType.value,
        enabled: formData.enabled,
      };

      if (isEditMode) {
        onUpdate(initialData.id, dataToSend);
      } else {
        onCreate(dataToSend);
      }

      handleClose();
    } catch (err) {
      if (err.inner) {
        const mappedErrors = {};

        err.inner.forEach((e) => {
          mappedErrors[e.path] = e.message;
        });

        setErrors(mappedErrors);
      }
    }
  };

  const ErrorWithIcon = ({ message }) => {
    if (!message) return null;

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <img src={VectorIcon} alt="error" width={14} height={14} />
        <span>{message}</span>
      </span>
    );
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;

    setFormData({ ...formData, [field]: value });

    if (field === 'name') {
      let error = '';

      if (!value.trim()) {
        error = 'Name is required';
      }
      setErrors((prev) => ({ ...prev, name: error }));
    }

    if (field === 'offsetValue') {
      let error = '';
      const num = Number(value);

      if (!value) {
        error = 'Offset value is required';
      } else if (isNaN(num)) {
        error = 'Must be a number';
      } else if (num < 1 || num > 10000) {
        error = 'Must be between 1 and 10000';
      }

      setErrors((prev) => ({ ...prev, offsetValue: error }));
    }
  };

  const handleSelectChange = (selectedOption) => {
    setFormData({ ...formData, offsetType: selectedOption });

    setErrors((prev) => ({
      ...prev,
      offsetType: selectedOption ? '' : 'Offset type is required',
    }));
  };

  const handleSwitchToggle = () => {
    setFormData({ ...formData, enabled: !formData.enabled });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title={isEditMode ? 'Edit Notification' : 'Create Notification'}
      footer
      closeIcon
      okText={isEditMode ? 'Update' : 'Create'}
      cancelText="Cancel"
    >
      <FormContainer>
        <div style={{ position: 'relative' }}>
          <Input
            label="Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            placeholder="Enter notification name"
            required
            maxLength={50}
            error={!!errors.name}
          />
          {errors.name && (
            <ErrorText>
              <ErrorWithIcon message={errors.name} />
            </ErrorText>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <Input
            label="Offset Value"
            value={formData.offsetValue}
            onChange={handleInputChange('offsetValue')}
            placeholder="Enter value (1-10000)"
            type="number"
            required
            error={!!errors.offsetValue}
          />
          {errors.offsetValue && (
            <ErrorText>
              <ErrorWithIcon message={errors.offsetValue} />
            </ErrorText>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <Select
            label="Offset Type"
            value={formData.offsetType}
            onChange={handleSelectChange}
            options={offsetTypeOptions}
            placeholder="Select offset type"
          />
          {errors.offsetType && (
            <ErrorText>
              <ErrorWithIcon message={errors.offsetType} />
            </ErrorText>
          )}
        </div>

        <SwitchWrapper>
          <Switch isOn={formData.enabled} onToggle={handleSwitchToggle} />
          {formData.enabled ? 'Enabled' : 'Disabled'}
        </SwitchWrapper>
      </FormContainer>
    </Modal>
  );
};

export default NotificationModal;
